import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  ERPIntegrationProviderMetadata,
  ERPIntegrationSync,
  ERPIntegrationSyncStatus,
  useERPIntegrationsStore,
} from "@/store/erpIntegrationsStore";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { JSONObject, JSONValue } from "@/components/jsonSchemaForm/types";
import { ERPSyncFormFields } from "@/components/erp/ERPSyncFormFields";
import {
  buildMissingPipelineWarning,
  filterCustomFieldMappingEnumsByCurrentConfig,
  filterOpportunityFieldMappingEnumsByCurrentConfig,
} from "@/components/erp/hubspotSyncHelpers";
import { useResolvedSyncConfigSchema } from "@/components/erp/utils";
import { useFeatureFlag } from "@/store/featureFlagStore";
import type { JSONSchema7, JSONSchema7Definition } from "json-schema";

type ERPSyncSheetProps = {
  /** Whether the sheet is open */
  open: boolean;
  /** Called when the sheet open state changes */
  onOpenChange: (open: boolean) => void;
} & (
  | {
      mode: "create";
      /** Available ERP providers to choose from */
      providers: ERPIntegrationProviderMetadata[];
      /** Called after a successful create so the parent can refetch */
      onCreated?: () => void;
    }
  | {
      mode: "edit";
      /** Sync being edited; when null the sheet stays empty */
      sync: ERPIntegrationSync | null;
      /** Called after a successful save so the parent can refetch */
      onSaved?: () => void;
    }
);

/**
 * Slide-over sheet for creating or editing an ERP sync. In create mode the
 * user picks a provider + connection before configuring sync settings; in
 * edit mode those are fixed and only the sync settings are mutable. Both
 * flows save the form in a single request so the row never lands in a
 * partially-saved state.
 * @param props - ERPSyncSheetProps
 * @returns Sync sheet element
 */
export function ERPSyncSheet(props: ERPSyncSheetProps) {
  const { open, onOpenChange } = props;
  const isCreate = props.mode === "create";
  const editSync = props.mode === "edit" ? props.sync : null;
  const providers = props.mode === "create" ? props.providers : [];

  const hubspotEnabled = useFeatureFlag("hubspot-erp-integration");
  const salesforceEnabled = useFeatureFlag("salesforce-erp-integration");
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );
  const prophet21Enabled = useFeatureFlag("prophet21-support");
  const jobBoss2Enabled = useFeatureFlag("jobboss2-erp-integration");
  const odysseyEnabled = useFeatureFlag("odyssey-erp-integration");
  const eniteoSupportEnabled = useFeatureFlag("eniteo-support");
  const visibleProviders = useMemo(
    () =>
      providers.filter((provider) => {
        if (provider.id === "hubspot" && !hubspotEnabled) {
          return false;
        }
        if (provider.id === "salesforce" && !salesforceEnabled) {
          return false;
        }
        if (provider.id === "epicor" && !epicorPrimitiveSupportEnabled) {
          return false;
        }
        if (provider.id === "prophet21" && !prophet21Enabled) {
          return false;
        }
        if (provider.id === "jobboss2" && !jobBoss2Enabled) {
          return false;
        }
        if (provider.id === "odyssey" && !odysseyEnabled) {
          return false;
        }
        if (provider.id === "eniteo" && !eniteoSupportEnabled) {
          return false;
        }
        return true;
      }),
    [
      providers,
      hubspotEnabled,
      salesforceEnabled,
      epicorPrimitiveSupportEnabled,
      prophet21Enabled,
      jobBoss2Enabled,
      odysseyEnabled,
      eniteoSupportEnabled,
    ],
  );

  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [status, setStatus] = useState<ERPIntegrationSyncStatus>("ENABLED");
  const [autoPushEnabled, setAutoPushEnabled] = useState(false);
  const [userEdits, setUserEdits] = useState<JSONObject>({});
  const [configEdited, setConfigEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createSync = useERPIntegrationsStore((state) => state.createSync);
  const updateSync = useERPIntegrationsStore((state) => state.updateSync);
  const fetchSyncs = useERPIntegrationsStore((state) => state.fetchSyncs);

  const selectedProvider = useMemo(
    () =>
      visibleProviders.find((provider) => provider.id === selectedProviderId) ??
      null,
    [visibleProviders, selectedProviderId],
  );

  const activeProvider = isCreate
    ? selectedProvider
    : (editSync?.provider ?? null);
  const activeConnectionId = isCreate
    ? selectedConnectionId || null
    : (editSync?.connectionId ?? null);

  const staticSchema = useMemo<JSONSchema7 | null>(
    () =>
      activeProvider?.syncConfigSchema
        ? (activeProvider.syncConfigSchema as JSONSchema7)
        : null,
    [activeProvider],
  );

  const {
    schema: resolvedSchema,
    canScan,
    capabilities,
    isLoading: isResolvingSchema,
    error: resolveSchemaError,
    refetch: refetchResolvedSchema,
  } = useResolvedSyncConfigSchema(
    activeProvider?.id ?? null,
    activeConnectionId,
  );

  const baseSchema: JSONSchema7 | null = isCreate
    ? selectedConnectionId
      ? (resolvedSchema ?? staticSchema)
      : null
    : (resolvedSchema ?? staticSchema);

  const defaults = useMemo(
    () => buildDefaultConfig(resolvedSchema ?? staticSchema),
    [resolvedSchema, staticSchema],
  );

  const config = useMemo(
    () => ({ ...defaults, ...userEdits }),
    [defaults, userEdits],
  );

  const configSchema = useMemo<JSONSchema7 | null>(() => {
    const withCustomFieldFiltered =
      filterCustomFieldMappingEnumsByCurrentConfig(baseSchema, config);
    return filterOpportunityFieldMappingEnumsByCurrentConfig(
      withCustomFieldFiltered,
      config,
    );
  }, [baseSchema, config]);

  const missingPipelineWarning = useMemo(
    () =>
      isCreate ? null : buildMissingPipelineWarning(resolvedSchema, config),
    [isCreate, resolvedSchema, config],
  );

  const missingRequiredFields = useMemo(
    () => getMissingRequiredFields(baseSchema, config),
    [baseSchema, config],
  );

  useEffect(() => {
    if (!isCreate || open) {
      return;
    }
    setSelectedProviderId("");
    setSelectedConnectionId("");
    setStatus("ENABLED");
    setAutoPushEnabled(false);
    setUserEdits({});
    setError(null);
    setIsSubmitting(false);
  }, [isCreate, open]);

  useEffect(() => {
    if (!isCreate) {
      return;
    }
    if (!selectedProviderId && visibleProviders.length > 0) {
      setSelectedProviderId(visibleProviders[0].id);
      return;
    }
    if (
      selectedProviderId &&
      !visibleProviders.some((provider) => provider.id === selectedProviderId)
    ) {
      setSelectedProviderId(visibleProviders[0]?.id ?? "");
    }
  }, [isCreate, visibleProviders, selectedProviderId]);

  useEffect(() => {
    if (!isCreate) {
      return;
    }
    if (!selectedProvider) {
      setSelectedConnectionId("");
      setUserEdits({});
      return;
    }
    setSelectedConnectionId("");
    setError(null);
    setUserEdits({});
  }, [isCreate, selectedProvider]);

  useEffect(() => {
    if (!editSync) {
      return;
    }
    setStatus(editSync.status);
    setAutoPushEnabled(editSync.autoPushEnabled);
    setUserEdits((editSync.integrationConfig ?? {}) as JSONObject);
    setConfigEdited(false);
    setError(null);
    setIsSubmitting(false);
  }, [editSync]);

  const handleSubmit = useCallback(async () => {
    if (props.mode === "create") {
      if (!selectedProvider) {
        setError("Please select an ERP provider.");
        return;
      }
      if (!selectedConnectionId) {
        setError("Please select a connection to use for this sync.");
        return;
      }
      if (missingRequiredFields.length > 0) {
        setError(buildMissingFieldsMessage(missingRequiredFields));
        return;
      }

      setIsSubmitting(true);
      setError(null);

      const result = await createSync({
        erpIntegrationProviderId: selectedProvider.id,
        connectionId: selectedConnectionId,
        status,
        integrationConfig:
          config && Object.keys(config).length > 0
            ? (config as Record<string, unknown>)
            : null,
        autoPushEnabled,
      });

      if (result.ok) {
        toast.success("Sync created.");
        await fetchSyncs();
        props.onCreated?.();
        onOpenChange(false);
      } else {
        toast.error(result.error);
      }

      setIsSubmitting(false);
      return;
    }

    if (!editSync) {
      return;
    }

    if (configEdited && missingRequiredFields.length > 0) {
      setError(buildMissingFieldsMessage(missingRequiredFields));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const success = await updateSync(editSync.id, {
      status,
      autoPushEnabled,
      ...(configEdited && {
        integrationConfig: Object.keys(config).length > 0 ? config : null,
      }),
    });

    if (success) {
      toast.success("Sync updated.");
      props.onSaved?.();
      onOpenChange(false);
    } else {
      setError("Failed to update sync. Please try again.");
    }

    setIsSubmitting(false);
  }, [
    props,
    selectedProvider,
    selectedConnectionId,
    editSync,
    status,
    autoPushEnabled,
    config,
    configEdited,
    missingRequiredFields,
    createSync,
    updateSync,
    fetchSyncs,
    onOpenChange,
  ]);

  const showFormFields = isCreate ? !!selectedConnectionId : !!editSync;
  const isSubmitDisabled =
    isSubmitting ||
    ((isCreate || configEdited) &&
      (isResolvingSchema ||
        !!resolveSchemaError ||
        missingRequiredFields.length > 0)) ||
    (isCreate && (!selectedProvider || !selectedConnectionId));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>{isCreate ? "New sync" : "Edit sync"}</SheetTitle>
          <SheetDescription>
            {isCreate
              ? "Pick a provider and connection. Sync settings can be edited later."
              : `${editSync?.provider?.name ?? "Update sync settings"}${
                  editSync?.connection.name
                    ? ` · ${editSync.connection.name}`
                    : ""
                }`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {isCreate && (
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="sync-provider">ERP Provider</Label>
                <Select
                  value={selectedProviderId}
                  onValueChange={setSelectedProviderId}
                >
                  <SelectTrigger id="sync-provider">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Connection</Label>
                {selectedProvider ? (
                  <ConnectionSelector
                    appId={selectedProvider.requiredConnectionProvider.appId}
                    providerId={
                      selectedProvider.requiredConnectionProvider.providerId
                    }
                    connectionId={selectedConnectionId || undefined}
                    onSelect={setSelectedConnectionId}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Select a provider to choose a connection.
                  </p>
                )}
              </div>
            </div>
          )}

          {showFormFields && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="sync-status"
                  className="flex items-center gap-1.5"
                >
                  <span
                    className={cn(
                      "inline-block h-2 w-2 rounded-full transition-all duration-300",
                      status === "ENABLED"
                        ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.45)]"
                        : "bg-muted-foreground/40",
                    )}
                  />
                  Sync Status
                </Label>
                <Select
                  value={status}
                  onValueChange={(value) =>
                    setStatus(value as ERPIntegrationSyncStatus)
                  }
                >
                  <SelectTrigger id="sync-status" className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ENABLED">Enabled</SelectItem>
                    <SelectItem value="DISABLED">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-0.5">
                  <Label
                    htmlFor="auto-push-toggle"
                    className="text-sm font-medium"
                  >
                    Auto-push to ERP
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    Automatically push new and updated enabled entities
                  </span>
                </div>
                <Switch
                  id="auto-push-toggle"
                  checked={autoPushEnabled}
                  onCheckedChange={setAutoPushEnabled}
                />
              </div>
            </div>
          )}

          {showFormFields && resolveSchemaError ? (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>{resolveSchemaError}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={refetchResolvedSchema}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : showFormFields && isResolvingSchema ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Loading sync configuration</span>
            </div>
          ) : showFormFields ? (
            <>
              {missingPipelineWarning && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{missingPipelineWarning}</span>
                </div>
              )}
              <ERPSyncFormFields
                config={config}
                onConfigChange={(next) => {
                  setUserEdits(next);
                  setConfigEdited(true);
                  setError(null);
                }}
                configSchema={configSchema}
                canScan={canScan}
                capabilities={capabilities}
                activeProvider={activeProvider}
              />
            </>
          ) : null}

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <SheetFooter className="border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {isCreate ? "Create sync" : "Save changes"}
            {isSubmitting && (
              <Loader2 className="ml-2 h-3.5 w-3.5 animate-spin" />
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Builds a default JSONObject for a provider's syncConfigSchema. Uses each
 * property's `default` when present, recurses into nested objects, falls back
 * to `false` for booleans, and omits the key otherwise. Returns an empty
 * object when no schema is provided.
 * @param schema - The provider's syncConfigSchema, or null
 * @returns A JSONObject with defaults pre-populated
 */
function buildDefaultConfig(schema: JSONSchema7 | null): JSONObject {
  if (!schema || schema.type !== "object" || !schema.properties) {
    return {};
  }
  const defaults: JSONObject = {};
  Object.entries(schema.properties).forEach(([key, value]) => {
    const propertySchema = value as JSONSchema7 & {
      default?: unknown;
      type?: string | string[];
    };
    if (!propertySchema) {
      return;
    }
    if (propertySchema.default !== undefined) {
      defaults[key] = propertySchema.default as JSONValue;
      return;
    }
    // Recurse into nested objects so leaf-level defaults (e.g. opportunityFieldMapping fields) are collected
    if (propertySchema.type === "object" && propertySchema.properties) {
      const nested = buildDefaultConfig(propertySchema as JSONSchema7);
      if (Object.keys(nested).length > 0) {
        defaults[key] = nested;
      }
      return;
    }
    if (
      propertySchema.type === "boolean" ||
      (Array.isArray(propertySchema.type) &&
        propertySchema.type.includes("boolean"))
    ) {
      defaults[key] = false;
    }
  });
  return defaults;
}

/**
 * Walks a sync-config schema's `required` array and returns the dotted paths of
 * any required fields that are missing or empty in the current config. For
 * required object fields (e.g. `stageMapping`), descends one level so each of
 * the nested object's own `required` keys is checked individually.
 * @param schema - The resolved (or static) sync-config schema, or null
 * @param config - The current form config
 * @returns The list of missing required field paths
 */
function getMissingRequiredFields(
  schema: JSONSchema7 | null,
  config: JSONObject,
): string[] {
  if (!schema || schema.type !== "object" || !schema.properties) {
    return [];
  }
  const required = Array.isArray(schema.required) ? schema.required : [];
  const missing: string[] = [];

  required.forEach((key) => {
    const propDef = schema.properties?.[key];
    const value = config[key];

    if (isObjectSchema(propDef)) {
      const nestedConfig = isJSONObject(value) ? value : {};
      const nestedRequired = Array.isArray(propDef.required)
        ? propDef.required
        : [];
      nestedRequired.forEach((nestedKey) => {
        if (isEmptyValue(nestedConfig[nestedKey])) {
          missing.push(`${key}.${nestedKey}`);
        }
      });
      return;
    }

    if (isEmptyValue(value)) {
      missing.push(key);
    }
  });

  return missing;
}

/**
 * Returns true when a JSON value should be treated as "not provided" for the
 * purposes of required-field validation: undefined, null, or an empty string.
 * @param value - Any JSON value
 * @returns Whether the value counts as empty/missing
 */
function isEmptyValue(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

/**
 * Builds a human-readable error message listing the missing required fields.
 * @param missingFields - Dotted paths of missing required fields
 * @returns A user-facing error string
 */
function buildMissingFieldsMessage(missingFields: string[]): string {
  return `Please complete required fields: ${missingFields.join(", ")}.`;
}

/**
 * Type guard: returns true when the value is a non-array, non-null JSONObject.
 * @param value - Any JSON value
 * @returns Whether value is a plain JSON object
 */
function isJSONObject(value: unknown): value is JSONObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Type guard: returns true when the schema definition is an `object` schema
 * (i.e. has a `properties` map we can descend into).
 * @param def - Any JSON schema definition
 * @returns Whether the definition is a plain object schema
 */
function isObjectSchema(
  def: JSONSchema7Definition | undefined,
): def is JSONSchema7 {
  return (
    typeof def === "object" &&
    def !== null &&
    !Array.isArray(def) &&
    def.type === "object"
  );
}
